import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, Plus, Minus, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  q: 'What is a cassette air conditioning unit?',
  a: 'A cassette unit is mounted into the ceiling, with the bulk of the equipment hidden above the suspended ceiling line. Only the decorative fascia panel is visible, distributing air downwards and outwards.'
}, {
  q: 'Does it distribute air evenly?',
  a: 'Yes, modern roundflow or 4-way cassette units distribute air 360 degrees, ensuring even temperature control without creating uncomfortable drafts for people sitting below.'
}, {
  q: 'Do I need a suspended ceiling?',
  a: 'Ideally, yes. Cassette units are designed to fit into standard ceiling grids (like 600x600mm). If you have a solid ceiling, a suspended bulkhead can be built, or a suspended unit might be recommended instead.'
}, {
  q: 'Are they energy efficient?',
  a: "Very. Premium brands like Daikin offer highly efficient inverter-driven cassette systems that adapt to the room's heat load, saving significant energy compared to older fixed-speed systems."
}, {
  q: 'How often do they need servicing?',
  a: 'For commercial environments, we recommend bi-annual servicing. The fascia panel drops down easily, allowing our engineers quick access to clean filters and check components.'
}];
export function Cassette() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2007">
      {/* 1. Hero Section */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2008">
        <div className="container mx-auto px-6" data-id="element-2009">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2010">
            <div className="lg:col-span-7" data-id="element-2011">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2012">
                Systems / Cassette
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-2013">
                Cassette air conditioning
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed mb-10" data-id="element-2014">
                Ceiling-mounted systems delivering 360-degree airflow. The
                perfect balance of performance and aesthetics for open
                commercial spaces.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2015">
                <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2016">
                  Book a site survey
                </Link>
                <Link to="/contact" className="bg-white hover:bg-primary-dark/5 text-primary-dark border border-primary-dark/10 px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2017">
                  Speak to our team
                </Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5" data-id="element-2018">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6
            }} className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg" data-id="element-2019">
                <img src="https://cdn.magicpatterns.com/uploads/kUteWPqLmbhAZP4Y92BuJK/cassette-detail.png" alt="Cassette air conditioning unit detail" className="w-full h-full object-cover" data-id="element-2020" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Best For */}
      <section className="bg-white py-16 border-b border-primary-dark/5" data-id="element-2021">
        <div className="container mx-auto px-6" data-id="element-2022">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2023">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary-dark/40 mb-6" data-id="element-2024">
              Best for
            </h2>
            <p className="text-xl md:text-2xl text-primary-dark/80 leading-relaxed font-medium" data-id="element-2025">
              Open-plan offices, retail stores, restaurants, reception areas,
              and customer-facing commercial spaces.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Key Benefits */}
      <section className="bg-[#E8F0F8]/50 py-24" data-id="element-2026">
        <div className="container mx-auto px-6" data-id="element-2027">
          <div className="max-w-4xl mx-auto" data-id="element-2028">
            <h2 className="text-3xl md:text-4xl font-serif mb-10 text-center" data-id="element-2029">
              Key benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-2030">
              {['360° even airflow distribution', 'Flush ceiling mount for clean aesthetics', 'Individual zone control capabilities', 'Highly energy efficient inverter technology', 'Low noise operation', 'Frees up valuable wall and floor space'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-2031">
                  <CheckCircle className="w-6 h-6 text-accent shrink-0" data-id="element-2032" />
                  <span className="font-medium text-primary-dark/80" data-id="element-2033">
                    {benefit}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Is this the right fit? */}
      <section className="bg-white py-24" data-id="element-2034">
        <div className="container mx-auto px-6" data-id="element-2035">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2036">
            Is this the right fit?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2037">
            <div className="bg-[#E8F0F8] p-10 rounded-3xl border border-primary-dark/5" data-id="element-2038">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-success" data-id="element-2039">
                <CheckCircle className="w-7 h-7" data-id="element-2040" />
                Good fit for
              </h3>
              <ul className="space-y-4" data-id="element-2041">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2042">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2043" />
                  <span data-id="element-2044">
                    Open commercial spaces requiring even air distribution
                  </span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2045">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2046" />
                  <span data-id="element-2047">Shops and retail environments</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2048">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2049" />
                  <span data-id="element-2050">Restaurants and cafes</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2051">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2052" />
                  <span data-id="element-2053">Spaces with standard grid suspended ceilings</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-primary-dark/10 shadow-sm" data-id="element-2054">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-error" data-id="element-2055">
                <XCircle className="w-7 h-7" data-id="element-2056" />
                May not be ideal for
              </h3>
              <ul className="space-y-4" data-id="element-2057">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2058">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2059" />
                  <span data-id="element-2060">Rooms with solid ceilings and no void space</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2061">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2062" />
                  <span data-id="element-2063">
                    Very small spaces where a wall unit is more cost-effective
                  </span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2064">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2065" />
                  <span data-id="element-2066">
                    Environments with exposed industrial aesthetics (unless
                    suspended)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AccuTemp design approach */}
      <section className="bg-primary-dark text-white py-24" data-id="element-2067">
        <div className="container mx-auto px-6" data-id="element-2068">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2069">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-2070">
              The AccuTemp design approach
            </h2>
            <p className="text-lg text-white/80 leading-relaxed" data-id="element-2071">
              We don't just install systems; we engineer solutions. Our in-house
              commercial team helps specify the exact cassette setup you need
              based on your room layout, comfort requirements, airflow dynamics,
              heat load, usage patterns, and budget. This design-led expertise
              ensures your system operates efficiently, quietly, and reliably
              for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FAQs */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2072">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-2073">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2074">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-2075">
            {faqs.map((faq, i) => <div key={i} className="border border-primary-dark/10 rounded-2xl overflow-hidden bg-white" data-id="element-2076">
                <button onClick={() => setOpenFaqId(openFaqId === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary-light/30 transition-colors" data-id="element-2077">
                  <span className="font-medium text-primary-dark pr-8" data-id="element-2078">
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0 text-primary-dark/40" data-id="element-2079">
                    {openFaqId === i ? <Minus className="w-5 h-5" data-id="element-2080" /> : <Plus className="w-5 h-5" data-id="element-2081" />}
                  </span>
                </button>
                <AnimatePresence data-id="element-2082">
                  {openFaqId === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-2083">
                      <div className="px-6 pb-6 pt-2 text-primary-dark/70 leading-relaxed border-t border-primary-dark/5" data-id="element-2084">
                        <p data-id="element-2085">{faq.a}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Brochure / literature section */}
      <section className="bg-white py-24 border-b border-primary-dark/5" data-id="element-2086">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2087">
          <h2 className="text-2xl font-serif mb-8" data-id="element-2088">Technical literature</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-2089">
            <a href="https://accutemp.co.uk/products/Compact%20Roundflow%20cassette%20-%20FCAG-B%20RZASG.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2090">
              <FileText className="w-5 h-5 text-accent" data-id="element-2091" />
              Download brochure
            </a>
            <a href="https://accutemp.co.uk/products/Commerical%20Cat.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2092">
              <Download className="w-5 h-5 text-accent" data-id="element-2093" />
              View commercial catalogue
            </a>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA */}
      <section className="bg-[#D4DDE8] py-24 text-center" data-id="element-2094">
        <div className="container mx-auto px-6" data-id="element-2095">
          <div className="max-w-2xl mx-auto" data-id="element-2096">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-10" data-id="element-2097">
              Ready to explore cassette options?
            </h2>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-2098">
              <Link to="/contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2099">
                Book a site survey
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2100" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-3 bg-white hover:bg-white/80 text-primary-dark px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2101">
                Get expert advice
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}